import React, { SyntheticEvent, useCallback, useState } from 'react';

import { Phone, ValidationError } from '../../../api-client';
import logoImage from '../../../assets/images/svg/conomica-cession.svg';
import {
  BoldText,
  Button,
  ConfirmSmsCodeForm,
  InputErrors,
  InputNumber,
  InputPassword,
  Loader,
  PrimaryButton,
} from '../../../components';
import { Image, WithinBlockDecorator } from '../../../components';
import CloseButton from '../../../components/common/buttons/decorators/CloseButton';
import { PrimaryButtonColor } from '../../../components/common/buttons/decorators/PrimaryButton/PrimaryButton';
import { createResetPasswordManager } from '../../../di';
import { useEffectOnUpdate, useNavigateByName, useNotifier } from '../../../hooks';
import { Errors as ValidationErrors, RouteDictionary } from '../../../utils';
import { sleep } from '../../../utils';

import './ForgotPassword.scoped.scss';

type FormErrors = {
  phone?: ValidationErrors;
  newPass?: ValidationErrors;
  confirmNewPass?: ValidationErrors;
};

enum ForgotPasswordSteps {
  EnterPhone = 0,
  ConfirmSms = 1,
  SetNewPassword = 2,
}

const ForgotPassword = () => {
  const [step, setStep] = useState(ForgotPasswordSteps.EnterPhone);
  const [phone, setPhone] = useState<Phone>(new Phone('+7', ''));
  const [newPass, setNewPass] = useState('');
  const [confirmNewPass, setConfirmNewPass] = useState('');
  const [errors, setErrors] = useState<FormErrors>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [confirmationUuid, setConfirmationUuid] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const notifier = useNotifier();
  const navigate = useNavigateByName();

  const handleError = useCallback(
    (error: any) => {
      if (error instanceof ValidationError) {
        notifier.createError(error.message || 'Ошибка валидации');
        return;
      }
      const message = error?.message || 'Что-то пошло не так';
      notifier.createError(message);
    },
    [notifier]
  );

  useEffectOnUpdate(() => {
    validateForm();
  }, [phone, newPass, confirmNewPass]);

  const validateForm = (): boolean => {
    const errors: FormErrors = {};
    if (step === ForgotPasswordSteps.EnterPhone && !phone.isValid()) {
      errors.phone = ['Номер телефона обязателен для заполнения'];
    }
    if (step === ForgotPasswordSteps.SetNewPassword) {
      if (newPass === '') {
        errors.newPass = ['Укажите новый пароль'];
      }
      if (confirmNewPass === '') {
        errors.confirmNewPass = ['Подтвердите новый пароль'];
      } else if (newPass !== confirmNewPass) {
        errors.confirmNewPass = ['Пароли должны совпадать'];
      }
    }
    setErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handlePhoneSubmit = async (event: SyntheticEvent) => {
    event.preventDefault();
    if (validateForm()) {
      setLoading(true);
      setError(false);
      try {
        const manager = createResetPasswordManager();
        const result = await manager.request(phone);
        setConfirmationUuid(result.uuid);
        setStep(ForgotPasswordSteps.ConfirmSms);
      } catch (err: any) {
        handleError(err);
        setError(true);
      } finally {
        setLoading(false);
      }
    }
  };

  const sendSms = async (smsCode: string) => {
    setVerificationCode(smsCode);
    setStep(ForgotPasswordSteps.SetNewPassword);
  };

  const resendSms = async () => {
    //TODO: ADD RESENDING SMS
    await sleep(3000);
  };

  const successCallback = () => {
    setStep(ForgotPasswordSteps.SetNewPassword);
  };

  const handlePasswordSubmit = async (event: SyntheticEvent) => {
    event.preventDefault();
    if (validateForm()) {
      setLoading(true);
      try {
        const resetPasswordManager = createResetPasswordManager();
        await resetPasswordManager.confirm(verificationCode, newPass);
        notifier.createSuccess('Ваш пароль был успешно изменен');
        navigate(RouteDictionary.ENTER);
      } catch (error: any) {
        handleError(error);
      } finally {
        setLoading(false);
      }
    }
  };

  const onClose = () => {
    navigate(RouteDictionary.ENTER);
  };

  return (
    <div className="forgot-password-page">
      <div className="forgot-password-page__close">
        <CloseButton>
          <Button onClick={onClose} />
        </CloseButton>
      </div>
      <div className="forgot-password-page__form">
        <div className="forgot-password-page__image-container">
          <WithinBlockDecorator>
            <Image src={logoImage} alt="Conomica цессии" className="forgot-password-page__image" />
          </WithinBlockDecorator>
        </div>
        {step === ForgotPasswordSteps.EnterPhone && (
          <div>
            <BoldText>
              <div className="forgot-password-page__heading">Восстановление пароля</div>
            </BoldText>
            <div className="forgot-password-page__text">
              Введите номер телефона, привязанный к вашему аккаунту
            </div>
            <form onSubmit={handlePhoneSubmit} className="forgot-password-page__form-content">
              <div className="form__group">
                <InputNumber
                  phone={phone}
                  onPhoneInput={setPhone}
                  helpingText="Используется для связи с платформой"
                  type="forgot-password-phone"
                />
                {errors.phone && <InputErrors errors={errors.phone} />}
              </div>
              {error && (
                <div className="form__group">
                  <InputErrors errors={['Произошла ошибка при отправке формы']} />
                </div>
              )}
              <div className="form__group">
                <PrimaryButton expanded large color={PrimaryButtonColor.GREEN}>
                  <Button type="submit" disabled={!phone.isValid() || loading}>
                    <div className="forgot-password-page__button-content">
                      {loading && <Loader size="small" overlay={false} />}
                      <span>{loading ? 'Отправка...' : 'Продолжить'}</span>
                    </div>
                  </Button>
                </PrimaryButton>
              </div>
            </form>
          </div>
        )}
        {step === ForgotPasswordSteps.ConfirmSms && (
          <div>
            <BoldText>
              <div className="forgot-password-page__heading">Подтверждение</div>
            </BoldText>
            <div className="forgot-password-page__text">
              Введите код из SMS, отправленный на ваш номер телефона
            </div>
            {error && (
              <div className="form__group">
                <InputErrors errors={['Произошла ошибка при отправке формы']} />
              </div>
            )}
            <ConfirmSmsCodeForm
              sendCallback={sendSms}
              resendCallback={resendSms}
              successCallback={successCallback}
              prefix={`forgotPassword-${confirmationUuid}`}
              phone={phone}
            />
          </div>
        )}
        {step === ForgotPasswordSteps.SetNewPassword && (
          <div>
            <BoldText>
              <div className="forgot-password-page__heading">Новый пароль</div>
            </BoldText>
            <div className="forgot-password-page__text">
              Придумайте новый пароль для вашего аккаунта
            </div>
            <form className="forgot-password-page__form-content" onSubmit={handlePasswordSubmit}>
              <div className="form__group">
                <label htmlFor="newPass" className="form__label">
                  Новый пароль
                </label>
                <InputPassword
                  password={newPass}
                  onInput={setNewPass}
                  title="Новый пароль"
                  id="newPass"
                />
                {errors.newPass && <InputErrors errors={errors.newPass} />}
              </div>
              <div className="form__group">
                <label htmlFor="confirmNewPass" className="form__label">
                  Подтверждение пароля
                </label>
                <InputPassword
                  password={confirmNewPass}
                  onInput={setConfirmNewPass}
                  title="Подтвердите пароль"
                  id="confirmNewPass"
                />
                {errors.confirmNewPass && <InputErrors errors={errors.confirmNewPass} />}
              </div>
              <div className="form__group">
                <PrimaryButton expanded large color={PrimaryButtonColor.GREEN}>
                  <Button type="submit" disabled={loading}>
                    <div className="forgot-password-page__button-content">
                      {loading && <Loader size="small" overlay={false} />}
                      <span>{loading ? 'Сохранение...' : 'Сохранить пароль'}</span>
                    </div>
                  </Button>
                </PrimaryButton>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default ForgotPassword;
